import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '@env/environment';

/** One rendered chat bubble. `error` bubbles are local-only (never sent back). */
export interface CareGuideChatMessage {
  role: 'user' | 'assistant';
  content: string;
  error?: boolean;
}

/** Mirrors the backend response shape (`fallback` = canned FAQ answer). */
interface CareGuideChatReply {
  reply: string;
  fallback: boolean;
}

/** Mirrors GET /care-guide/starters. */
export interface CareGuideStartersReply {
  starters: string[];
}

/**
 * Patient AI-path caps (approved Care Guide AI MVP): 500 characters per
 * message, and only the last 4 prior turns + the current question are sent —
 * the backend enforces the same bounds server-side.
 */
export const MAX_MESSAGE_LENGTH = 500;
const MAX_HISTORY = 5;

const ERROR_REPLY = "The guide couldn't answer just now — please try again.";

/**
 * Session-only state + API client for the Care Guide chat widget.
 *
 * The conversation lives in memory ONLY (never localStorage / sessionStorage):
 * logout triggers a full page reload (see PrivateLayoutComponent), which
 * clears it. Chat content is educational, but we still treat it as private.
 */
@Injectable({ providedIn: 'root' })
export class CareGuideChatService {
  private readonly _open$ = new BehaviorSubject<boolean>(false);

  /** Whether the chat panel is open (the Help page opens it remotely). */
  public readonly open$: Observable<boolean> = this._open$.asObservable();

  /** Conversation so far, oldest first. In-memory only. */
  public messages: CareGuideChatMessage[] = [];

  /** True while a reply is in flight (input + send are disabled). */
  public sending = false;

  /**
   * Small page context for the AI path (approved plan §15): the ACP question
   * the patient currently has open — never answers, never the record. Set by
   * the widget when the panel opens; cleared when unknown.
   */
  public topic: string | null = null;
  public questionId: string | null = null;

  constructor(private readonly http: HttpClient) {}

  public open(): void {
    this._open$.next(true);
  }

  public close(): void {
    this._open$.next(false);
  }

  public get isOpen(): boolean {
    return this._open$.getValue();
  }

  /** Suggested questions — topic-aware when a topic is set (app content). */
  public getStarters(): Observable<CareGuideStartersReply> {
    const suffix = this.topic ? `?topic=${encodeURIComponent(this.topic)}` : '';
    return this.http.get<CareGuideStartersReply>(`${environment.apiUrl}/care-guide/starters${suffix}`);
  }

  /**
   * Sends a user turn: appends it, posts the bounded history and appends the
   * reply — or a friendly error bubble (never the raw error) on failure.
   */
  public send(text: string): void {
    const content = (text || '').trim().slice(0, MAX_MESSAGE_LENGTH);

    if (!content || this.sending) {
      return;
    }

    this.messages.push({ role: 'user', content });
    this.sending = true;

    // Last 4 prior turns + the current question (context minimization).
    const history = this.messages
      .filter((message) => !message.error)
      .map((message) => ({ role: message.role, content: message.content }))
      .slice(-MAX_HISTORY);

    // Per-submission id = the backend's duplicate protection: a double
    // submit of the same id can never produce two model calls. Backend must
    // receive a real uuid or nothing (it generates one when absent).
    const cryptoWithUuid = (typeof crypto !== 'undefined' ? crypto : undefined) as
      | { randomUUID?: () => string }
      | undefined;
    const requestId = cryptoWithUuid?.randomUUID ? cryptoWithUuid.randomUUID() : undefined;

    this.http
      .post<CareGuideChatReply>(`${environment.apiUrl}/care-guide/chat`, {
        messages: history,
        ...(this.topic ? { topic: this.topic } : {}),
        ...(this.questionId ? { questionId: this.questionId } : {}),
        ...(requestId ? { requestId } : {}),
      })
      .subscribe(
        (response) => {
          this.sending = false;
          this.messages.push({ role: 'assistant', content: response.reply });
        },
        () => {
          this.sending = false;
          this.messages.push({ role: 'assistant', content: ERROR_REPLY, error: true });
        },
      );
  }
}
